/**
 * The public donations API.
 *
 * Same-origin on purpose, exactly like api/states.ts: callbox resolves the
 * tenant from the subdomain and injects X-TENANT before Django sees the request.
 * The browser neither knows nor sends the tenant, so pointing these at another
 * origin would silently drop the header.
 */

export type PublicDonationConfig = {
  /** donations_enabled && charges_enabled — whether a gift can actually be taken. */
  is_ready: boolean;
  donations_enabled: boolean;
  charges_enabled: boolean;
  currency: string;
  min_amount_cents: number;
  /** Platform publishable key — safe in the browser; needed to init Stripe.js. */
  publishable_key: string;
  /** The connected account gifts are charged on (direct charges). */
  stripe_account_id: string;
};

export type DonationFrequency = 'one_time' | 'monthly';

export type DonationIntentRequest = {
  amount_cents: number;
  frequency: DonationFrequency;
  donor_email?: string;
  donor_name?: string;
};

export type DonationIntentResponse = {
  /** The PaymentIntent client secret the Payment Element confirms. */
  client_secret: string;
  payment_intent_id?: string;
  subscription_id?: string;
  customer_id?: string;
};

export async function fetchDonationConfig(signal?: AbortSignal): Promise<PublicDonationConfig> {
  const response = await fetch('/api/donations/public-config/', {
    signal,
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`public-config responded ${response.status}`);
  }
  return response.json();
}

/**
 * Start a gift. Rejects with an Error carrying the server's message on a 4xx so
 * the form can surface "minimum gift is …" or "not accepting donations".
 */
export async function createDonationIntent(
  body: DonationIntentRequest,
  signal?: AbortSignal,
): Promise<DonationIntentResponse> {
  const response = await fetch('/api/donations/intent/', {
    method: 'POST',
    signal,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new Error(await errorMessage(response));
  }
  return response.json();
}

async function errorMessage(response: Response): Promise<string> {
  try {
    const data = await response.json();
    if (typeof data?.detail === 'string') return data.detail;
    if (Array.isArray(data?.amount_cents)) return data.amount_cents[0];
    return `Donation failed (${response.status}).`;
  } catch {
    return `Donation failed (${response.status}).`;
  }
}
